import React from 'react'
import { Typography, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { numberFormat } from '@/helpers/numbers'
import { Currency, getCurrencySymbol } from '@caterdesk/utils--money'
import useTenant from '@/hooks/useTenant'

type Props = {
  currency: string
  bundle: number
  totalToPay: number
  onChange: (number: number) => void
}

const CreditSelector: React.FC<Props> = ({ bundle, onChange, totalToPay, currency }) => {
  const { t } = useTranslation()
  const tenant = useTenant()
  const topupValues = tenant.features?.ccTopupAmounts?.topupValues ?? []
  const minimumTopupValue = tenant.features?.ccTopupAmounts?.minimumTopup ?? 0
  const hasValidBundles = topupValues.some((topUpValue) => topUpValue && topUpValue >= totalToPay)

  return (
    <Stack width="100%" spacing={2} direction="column">
      {totalToPay >= minimumTopupValue && (
        <Stack spacing={1} direction="column">
          <Typography color="text.secondary" variant="body2">
            {t('cc:justWhatYouNeed')}
          </Typography>
          <ToggleButtonGroup
            exclusive
            orientation="vertical"
            value={bundle}
            onChange={(_event, newValue) => {
              if (newValue !== null) {
                onChange(newValue)
              }
            }}
            fullWidth
          >
            <ToggleButton
              value={0}
              sx={{
                borderRadius: 3,
                justifyContent: 'center',
                minHeight: 56,
                py: 2,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
              }}
            >
              {t('cc:buyCredits', {
                count: totalToPay,
                formattedCount: numberFormat(totalToPay, 2, false),
                amount: `${getCurrencySymbol(currency as Currency)}${numberFormat(totalToPay)}`,
              })}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}
      {hasValidBundles && (
        <Stack direction="column" spacing={1}>
          <Typography color="text.secondary" variant="body2">
            {totalToPay >= minimumTopupValue
              ? t('cc:creditBundle')
              : t('cc:minimumPayment', {
                  amount: `${getCurrencySymbol(currency as Currency)}${minimumTopupValue}`,
                })}
          </Typography>
          <ToggleButtonGroup
            exclusive
            orientation="vertical"
            value={bundle}
            onChange={(_event, newValue) => {
              if (newValue !== null) {
                onChange(newValue)
              }
            }}
            fullWidth
          >
            {topupValues.map((topUpValue) =>
              topUpValue && topUpValue >= totalToPay ? (
                <ToggleButton
                  key={topUpValue}
                  value={topUpValue}
                  sx={{
                    borderRadius: 3,
                    justifyContent: 'center',
                    minHeight: 56,
                    py: 2,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                  }}
                >
                  {t('cc:buyCredits', {
                    count: topUpValue,
                    formattedCount: numberFormat(topUpValue, 2, false),
                    amount: `${getCurrencySymbol(currency as Currency)}${numberFormat(topUpValue)}`,
                  })}
                </ToggleButton>
              ) : null,
            )}
          </ToggleButtonGroup>
        </Stack>
      )}
    </Stack>
  )
}

export default CreditSelector
